// @ts-check
// =============================================================================
// js/screens/duel/highlight-fx.js — targeting visuals v2: outline filters.
//
// THE LANGUAGE (replaces the ring/plate drawings — those are commented out
// at their call sites in board.js / hand.js / bars.js):
//   pickable  -> NEON TEAL outline on everything you can act on right now:
//                hand cards you can afford, ready creatures, and the
//                Draw / Harvest / Devour buttons while they're clickable.
//   selected  -> the outline turns GREEN on the thing you picked.
//   targeted  -> YELLOW-ORANGE outline on every legal target.
//   enemy turn / spectating -> no outlines at all (no sources = no states;
//                bars.js additionally gates the always-legal buttons).
//
// OutlineFilter (pixi-filters) traces the asset's rendered silhouette — a
// thick, crisp, detailed outline, not a blurry glow. One shared filter per
// state (filters are stateless across objects), resolution-inherited so it
// stays sharp on high-DPI.
// =============================================================================

import { OutlineFilter } from 'pixi-filters';

/** The state colors (also exported for anything drawing to match). */
export const HIGHLIGHT_TINTS = {
	pickable: 0x2ef5e2, // neon teal
	selected: 0x46ff7d, // green
	targeted: 0xffb347, // yellow-orange
	devourwin: 0xff4040, // red — the flashing Devour-to-win cue (matches deck-0 flash)
};

/**
 * @param {number} color
 * @returns {OutlineFilter}
 */
function makeOutline(color) {
	const filter = new OutlineFilter({ thickness: 3.5, color, quality: 0.6, alpha: 0.95 });
	// A filter rasterizes its subject at 1x by default — blurry on high-DPI.
	filter.resolution = 'inherit';
	filter.antialias = 'inherit';
	filter.padding = 8; // room for the outline outside the silhouette
	return filter;
}

const FILTERS = {
	pickable: makeOutline(HIGHLIGHT_TINTS.pickable),
	selected: makeOutline(HIGHLIGHT_TINTS.selected),
	targeted: makeOutline(HIGHLIGHT_TINTS.targeted),
	devourwin: makeOutline(HIGHLIGHT_TINTS.devourwin),
};

/**
 * Set (or clear) a display object's highlight outline. Any other filters on
 * the object are preserved; idempotent per state so the FEEDBACK pass can
 * call it every repaint.
 * @param {any} obj  A PIXI display object (tile wrap, hand cell, chip, …).
 * @param {'pickable'|'selected'|'targeted'|'devourwin'|null|false|undefined} highlight
 * @returns {void}
 */
export function applyHighlight(obj, highlight) {
	if (!obj || obj.destroyed) return;
	const state = FILTERS[highlight] ? highlight : null;
	if (obj.__highlightState === state) return;
	obj.__highlightState = state;
	const mine = Object.values(FILTERS);
	const others = (obj.filters ? [...obj.filters] : []).filter((f) => !mine.includes(f));
	obj.filters = state ? [...others, FILTERS[state]] : (others.length ? others : null);
}
